import { InstrumentedHttpDataResponse } from '../shared/instrumentation/instrumented-http.service';
import { FileItem, FileType } from '../shared/models/file-item.model';
import { HttpRequestOptions } from '../shared/models/http-request-options.model';
import { BaseResponse, clientRequestIdHeader, correlationIdResponseHeader, EdgeworthSubstrateDeltaSyncFile, EdgeworthSubstrateFile, RawUserRelationship, UserRelationship } from './edgeworth-data.model';

export function isEmailAttachment(file: EdgeworthSubstrateFile | EdgeworthSubstrateDeltaSyncFile) {
    return file && file.source === 'EmailAttachment';
}

export function getNativeUriScheme(fileType: FileType): string {
    switch (fileType) {
        case FileType.Word:
            return 'ms-word:ofe|u|';
        case FileType.Excel:
            return 'ms-excel:ofv|ofc|u|';
        case FileType.Visio:
            return 'ms-visio:ofe|u|';
        case FileType.PowerPoint:
            return 'ms-powerpoint:ofe|u|';
        case FileType.OneNote:
            return 'onenote:';
    }

    return null;
}

export function getFileType(doc: EdgeworthSubstrateFile): FileType {
    let filetype = FileType.None;
    if (doc.type === 'Custom') {
        filetype = FileType.ThirdParty;
    } else if (doc.type === 'Lists') {
        filetype = FileType.List;
    } else if (doc.type === 'Project' && doc.sub_type === 'ProjectOperations') {
        filetype = FileType.ProjectOperations;
    } else if (doc.extension) {
        filetype = FileItem.fileTypeFromExtension(doc.extension);
    } else {
        filetype = FileType[doc.type] || FileType.None;
    }

    if (!filetype || filetype === FileType.None) {
        filetype = FileItem.fileTypeNameToFileType(doc.type);
    }

    return filetype;
}

export function getUserActionsDateTime(doc: EdgeworthSubstrateDeltaSyncFile): Date {
    return doc.user_relationship?.last_action_datetime;
}

export function getAccessedDateTime(doc: EdgeworthSubstrateFile): Date {
    return doc.user_relationship?.last_access_datetime;
}

export function safeParseDate(dateString?: string): Date {
    try {
        if (dateString) {
            return new Date(dateString);
        } else {
            return null;
        }
    } catch {
        return null;
    }
}

export function processUserRelationship(rawUserRelationship: RawUserRelationship): UserRelationship {
    if (!rawUserRelationship) {
        return undefined;
    }

    return {
        last_access_datetime: safeParseDate(rawUserRelationship.last_access_datetime),
        last_modified_datetime: safeParseDate(rawUserRelationship.last_modified_datetime),
        last_shared_by_id: rawUserRelationship.last_shared_by_id,
        last_shared_datetime: safeParseDate(rawUserRelationship.last_shared_datetime),
        save_for_later_datetime: safeParseDate(rawUserRelationship.save_for_later_datetime),
        last_shared_by_history: rawUserRelationship.last_shared_by_history,
        last_shared_display_name: rawUserRelationship.last_shared_display_name,
        last_action_datetime: safeParseDate(rawUserRelationship.last_action_datetime)
    };
}

export function getServerDebugInfoString(response: InstrumentedHttpDataResponse<BaseResponse>): string {
    if (!response || !response.headers) {
        return '';
    }

    const headers = response.headers;
    const backendServer = headers.get('X-CalculatedBETarget') || '';
    const frontendServer = headers.get('X-FEServer') || '';
    const cid = headers.get(correlationIdResponseHeader);

    let extendedInfoString: string = '';

    const extendedInfo = response.data?.diagnostics_data?.extended_info;
    if (extendedInfo) {
        extendedInfoString = `WorkingSetFilesPlugin:${extendedInfo.WorkingSetFilesPlugin}, MruFilesPlugin:${extendedInfo.MruFilesPlugin}, Version:${extendedInfo.Version}`;
    }

    return `BE:${backendServer}, FE:${frontendServer}|${extendedInfoString}, CID:${cid}`;
}

export function createRequestConfig(mailbox: string, application: string, sessionKey: string, clientAppId: string, requestId: string, currentPage: string) : HttpRequestOptions {
    const headers = new Map<string, string | Array<string>>([
        ['X-AnchorMailbox', mailbox],
        ['X-Office-Application', application],
        ['X-Office-Scenario', currentPage],
        ['X-RoutingParameter-SessionKey', sessionKey],
        ['X-Ms-ClientAppId', clientAppId],
        [clientRequestIdHeader, requestId]
    ]);
    return {
        headers
    };
}

export function createS2SRequestConfig(requestId: string): HttpRequestOptions {
    const headers = new Map<string, string | Array<string>>([
        [clientRequestIdHeader, requestId]
    ]);
    return {
        headers
    };
}
